import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './DashboardDetailed.css';
import { useSelector } from 'react-redux';
import { FaClock, FaMoneyBill, FaFileAlt, FaEdit, FaHourglassHalf, FaTimesCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import {
  getTimesheetCount,
  getTimesheetCountsFromList,
  getCurrentYearDateRange,
} from '../../api/timesheets';
import {
  getExpenseCount,
  getPendingExpenseCountFromList,
} from '../../api/expenses';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const yearRange = useMemo(() => getCurrentYearDateRange(), []);
  const [counts, setCounts] = useState({
    draftTimeSheets: null,
    pendingTimeSheets: null,
    rejectedTimeSheets: null,
    pendingExpenses: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentDate = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    [i18n.language]
  );

  const orgId = user?.department?.costCenter?.organization?.orgId ?? 1;
  const userId = user?.userId ?? user?.id;

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setError(t('dashboard.notSignedIn'));
      return;
    }

    let cancelled = false;

    const loadTimesheetCounts = async () => {
      try {
        return await getTimesheetCount({
          orgId,
          userId,
          from: yearRange.from,
          to: yearRange.to,
        });
      } catch (countErr) {
        console.warn('timesheet/count failed, falling back to timesheet/all:', countErr);
        return getTimesheetCountsFromList({
          orgId,
          userId,
          from: yearRange.from,
          to: yearRange.to,
        });
      }
    };

    const loadExpenseCount = async () => {
      try {
        const fromCount = await getExpenseCount({
          orgId,
          userId,
          from: yearRange.from,
          to: yearRange.to,
        });
        if (typeof fromCount === 'number') return fromCount;
      } catch (countErr) {
        console.warn('expense/count failed, falling back to expense/all:', countErr);
      }
      return getPendingExpenseCountFromList({
        orgId,
        userId,
        from: yearRange.from,
        to: yearRange.to,
      });
    };

    const loadCounts = async () => {
      setLoading(true);
      setError(null);
      try {
        const [timesheetCounts, pendingExpenses] = await Promise.all([
          loadTimesheetCounts(),
          loadExpenseCount(),
        ]);

        if (cancelled) return;

        setCounts({
          draftTimeSheets: timesheetCounts.draft,
          pendingTimeSheets: timesheetCounts.pending,
          rejectedTimeSheets: timesheetCounts.rejected,
          pendingExpenses,
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Dashboard count fetch failed:', err);
        setError(err?.response?.data?.message || err?.message || t('dashboard.loadFailed'));
        setCounts({
          draftTimeSheets: null,
          pendingTimeSheets: null,
          rejectedTimeSheets: null,
          pendingExpenses: null,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [orgId, userId, yearRange.from, yearRange.to]);

  const formatCount = (value) => {
    if (loading && value === null) return '—';
    if (value === null) return '—';
    return value;
  };

  const summaryCards = [
    {
      key: 'draft',
      title: t('dashboard.draftTimeSheets'),
      value: counts.draftTimeSheets,
      color: 'blue',
      icon: <FaEdit />,
    },
    {
      key: 'pending-ts',
      title: t('dashboard.pendingTimeSheets'),
      value: counts.pendingTimeSheets,
      color: 'yellow',
      icon: <FaHourglassHalf />,
    },
    {
      key: 'rejected',
      title: t('dashboard.rejectedTimeSheets'),
      value: counts.rejectedTimeSheets,
      color: 'red',
      icon: <FaTimesCircle />,
    },
    {
      key: 'pending-exp',
      title: t('dashboard.pendingExpenses'),
      value: counts.pendingExpenses,
      color: 'green',
      icon: <FaMoneyBill />,
    },
  ];

  const quickLinks = [
    { path: '/timesheets', label: t('dashboard.timesheets'), icon: <FaClock /> },
    { path: '/expenses', label: t('dashboard.expenses'), icon: <FaMoneyBill /> },
    { path: '/documents', label: t('dashboard.documents'), icon: <FaFileAlt /> },
  ];

  return (
    <div className="dashboard-detailed-container">
      <div className="dashboard-welcome-section">
        <h1 className="dashboard-welcome-text">
          {t('dashboard.welcome', { name: user?.firstName || t('dashboard.defaultUser') })}
        </h1>
        <p className="dashboard-date-text">{currentDate}</p>
      </div>

      {error && <div className="dashboard-error-banner">{error}</div>}

      <div className="dashboard-cards-grid">
        {summaryCards.map((card) => (
          <div className="dashboard-card" key={card.key}>
            <div className={`dashboard-card-icon ${card.color}`}>
              {card.icon}
            </div>
            <div className="dashboard-card-content">
              <h3 className="dashboard-card-title">{card.title}</h3>
              <div className="dashboard-card-value">{formatCount(card.value)}</div>
            </div>
          </div>
        ))}
      </div>

      <h2 className="dashboard-section-title">{t('dashboard.quickLinks')}</h2>
      <div className="dashboard-quick-links">
        {quickLinks.map((item) => (
          <Link key={item.path} to={item.path} className="dashboard-quick-link">
            <span className="dashboard-quick-link-icon">{item.icon}</span>
            <span className="dashboard-quick-link-label">{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
